import {VNode} from 'snabbdom/vnode';
import {AttrsSchema, Component, jsx} from 'panel';

import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {SvgIconEl} from 'common/components/svg-icon';
import {TooltipEl} from 'common/components/tooltip';
import {Placement} from 'common/components/tooltip/types';
import {featuresStore} from 'common/report/stores/features';
import {ProjectFeatureFlag} from 'common/report/constants';

import css from './index.styl?inline';
import './index.styl';

export type SegmentationLimitInfoAttrsSchema = {
  'segmentation-limit': number;
  'export-segmentation-limit': number;
};

export interface LimitInfoJsxProps {
  attrs?: SegmentationLimitInfoAttrsSchema;
  on?: EmptyObj;
  props?: EmptyObj;
}

/*
 * Slightly opinionated icon and tooltip for reports that experience result truncation
 *  due to segmentation query limits.
 *
 */
export class SegmentationLimitInfo extends Component<EmptyObj, SegmentationLimitInfoAttrsSchema> {
  static get attrsSchema(): AttrsSchema<LimitInfoJsxProps['attrs']> {
    return {
      ...super.attrsSchema,
      'segmentation-limit': {
        type: `number`,
        description: `Required numeric value representing the query limit affecting this chart.`,
      },
      'export-segmentation-limit': {
        type: `number`,
        description: `Required numeric value representing the query limit applied to exporting the data behind this chart.`,
      },
    };
  }

  get config() {
    return {
      css,
      useShadowDom: true,
      template: () => this.renderTemplate(),
      defaultState: {},
    };
  }

  private get segmentationLimit(): number {
    return this.attr(`segmentation-limit`);
  }

  private get exportSegmentationLimit(): number {
    return this.attr(`export-segmentation-limit`);
  }

  private get learnMoreUrl(): string {
    // TODO pjboudrx update with final help center article, once available
    return `https://help.mixpanel.com/hc/en-us/articles/360001182866-Event-Property-Limitations-in-Reports`;
  }

  private renderTemplate(): VNode {
    if (!featuresStore.value?.hasProjectFeatureFlag(ProjectFeatureFlag.HighCardinality)) {
      return null;
    }
    return (
      <div sel='.segmentation-limit-info'>
        <SvgIconEl
          attrs={{
            icon: `info`,
          }}
        />
        <TooltipEl
          attrs={{
            'always-show': false,
            placement: Placement.Right,
            'max-width': `200px`,
          }}
        >
          <p>
            We limit the number of segments to {this.segmentationLimit.toLocaleString()}.&nbsp;
            <a attrs={{href: this.learnMoreUrl, target: `_blank`}}>Learn more</a>
            {this.tooltipExtraAction()}.
          </p>
        </TooltipEl>
      </div>
    );
  }

  private tooltipExtraAction() {
    return [
      ` or `,
      <a
        attrs={{
          href: `javascript:void(0);`,
        }}
        on={{
          click: () => this.dispatchEvent(new CustomEvent(`export-csv`, {bubbles: true, composed: true})),
        }}
      >
        export up to {this.exportSegmentationLimit.toLocaleString()} segments via CSV
      </a>,
    ];
  }
}

export const SegmentationLimitInfoEl = defineAndMakeJsxFactory(`mp-segmentation-limit-info`, SegmentationLimitInfo);
